import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { api, IMG } from '../lib/api';
import { useLang } from '../i18n';
import Seo from '../components/Seo';
import { Hero } from '../components/home/Hero';
import { SeasonSection } from '../components/home/SeasonSection';
import { CollectionsRail } from '../components/home/CollectionsRail';
import { ShadesFocus } from '../components/home/ShadesFocus';
import { MethodBuild, DualPath } from '../components/home/Sections';
import { ShowroomReel } from '../components/home/ShowroomReel';
import { Img } from '../components/Img';

export default function Home() {
  const { t, lang } = useLang();
  const [data, setData] = useState({ collections: [], shades: [], articles: [] });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, s, m] = await Promise.all([
          api.get(`/collections?lang=${lang}&featured=true`),
          api.get(`/shades?lang=${lang}`),
          api.get(`/magazine?lang=${lang}`),
        ]);
        if (!alive) return;
        setData({
          collections: c.data.items,
          shades: s.data,
          articles: m.data,
        });
      } catch (e) {
        console.error('home load failed', e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [lang]);

  const { collections, shades, articles } = data;

  return (
    <>
      <Seo
        title="Vivi di emozioni"
        description={t('home.lead')}
        image={IMG.hero}
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'VdE Tivoli 1996',
          alternateName: 'Villa d’Este Home Tivoli',
          slogan: 'Vivi di emozioni',
          foundingDate: '1996',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Tivoli',
            addressRegion: 'RM',
            addressCountry: 'IT',
          },
          url: window.location.origin,
        }}
      />

      <Hero />
      <SeasonSection />
      <CollectionsRail collections={collections} />
      <ShadesFocus shades={shades} />
      <MethodBuild />
      <ShowroomReel />

      {/* ─────────── ISPIRAZIONI ─────────── */}
      <section data-testid="home-magazine" className="py-16 md:py-24">
        <div className="wrap">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
            <h2 className="text-[30px] leading-[1.06] md:text-[40px] lg:text-[52px]">{t('home.magazineTitle')}</h2>
            <Link to="/storie/articoli" data-testid="home-all-articles" className="btn btn-line">
              {t('common.more')}
              <ArrowUpRight size={15} strokeWidth={2} />
            </Link>
          </div>
          {/* Griglia che va a capo, non una fila tagliata al bordo: una riga
              troncata si legge come un errore, non come uno scorrimento. */}
          <ul className="grid gap-10 md:grid-cols-3 md:gap-8">
          {articles.map((a) => (
            <li key={a.slug}>
              <Link
                to={`/storie/articoli/${a.slug}`}
                data-testid={`home-article-${a.slug}`}
                className="group block"
              >
                <div className="aspect-[3/2] overflow-hidden bg-carta-2">
                  <Img
                    src={a.image}
                    alt={a.title}
                    sizes="(max-width:768px) 100vw, 31vw"
                    className="img-zoom h-full w-full object-cover"
                  />
                </div>
                <h3 className="mt-5 font-display text-2xl leading-snug transition-colors group-hover:text-marsala">
                  {a.title}
                </h3>
                <p className="mt-2 text-sm text-inchiostro/65">{a.kicker}</p>
              </Link>
            </li>
          ))}
          </ul>
        </div>
      </section>

      <DualPath />
    </>
  );
}
